import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchJson } from "../../lib/api";
import { channels } from "../../lib/constants";
import type {
  House,
  HouseAccess,
  HouseInvite,
  CalendarFeedInfo,
  ChannelId,
  WeekStartDay,
  WorkerJobLog,
} from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Input } from "../input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";
import { ImportLogs } from "../channels/import-logs";
import { ChannelConnectModal } from "../channels/channel-connect-modal";

export const HouseCard = ({
  house,
  logs,
  onRefresh,
  onRefreshLogs,
}: {
  house: House;
  logs: WorkerJobLog[];
  onRefresh: () => void;
  onRefreshLogs?: () => void;
}) => {
  const { t } = useI18n();
  const [accessList, setAccessList] = useState<HouseAccess[]>([]);
  const [invites, setInvites] = useState<HouseInvite[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"readonly" | "full">("readonly");
  const [error, setError] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [connectedChannels, setConnectedChannels] = useState<ChannelId[]>([]);
  const [externalHouseIds, setExternalHouseIds] = useState<Partial<Record<ChannelId, string>>>({});
  const [activeModalChannel, setActiveModalChannel] = useState<ChannelId | null>(null);
  const [pendingImportChannel, setPendingImportChannel] = useState<ChannelId | null>(null);
  const [weekStartDay, setWeekStartDay] = useState<WeekStartDay>(
    house.weekStartDay ?? "friday",
  );
  const [calendarFeedUrl, setCalendarFeedUrl] = useState<string | null>(null);
  const [calendarFeedError, setCalendarFeedError] = useState<string | null>(null);
  const [calendarFeedNotice, setCalendarFeedNotice] = useState<string | null>(null);
  const [isCalendarFeedSaving, setIsCalendarFeedSaving] = useState(false);
  const houseLogs = logs.filter((log) => log.houseId === house.id);
  const isReadonly = house.role === "readonly";

  const loadAccess = useCallback(async () => {
    const data = await fetchJson<{ access: HouseAccess[]; invites: HouseInvite[] }>(
      "/api/houses/" + house.id + "/access",
    );
    setAccessList(data.access);
    setInvites(data.invites);
  }, [house.id]);

  const loadChannels = useCallback(async () => {
    const data = await fetchJson<{ channels: ChannelId[]; externalHouseIds?: Partial<Record<ChannelId, string>> }>(
      "/api/houses/" + house.id + "/credentials",
    );
    setConnectedChannels(data.channels);
    setExternalHouseIds(data.externalHouseIds ?? {});
  }, [house.id]);

  const loadCalendarFeed = useCallback(async () => {
    const data = await fetchJson<CalendarFeedInfo>("/api/houses/" + house.id + "/calendar-feed");
    setCalendarFeedUrl(data.url);
  }, [house.id]);

  useEffect(() => {
    if (isReadonly) {
      setAccessList([]);
      setInvites([]);
      setConnectedChannels([]);
      setCalendarFeedUrl(null);
      return;
    }
    void loadAccess();
    void loadChannels();
    void loadCalendarFeed();
  }, [isReadonly, loadAccess, loadCalendarFeed, loadChannels]);

  useEffect(() => {
    setWeekStartDay(house.weekStartDay ?? "friday");
  }, [house.weekStartDay]);

  const sendInvite = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      await fetchJson("/api/houses/" + house.id + "/invites", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      setInviteEmail("");
      await loadAccess();
    } catch (inviteError) {
      setError(
        inviteError instanceof Error ? inviteError.message : t("invite.failed", "Failed to invite"),
      );
    }
  };

  const revoke = async (inviteId: number) => {
    await fetchJson("/api/invites/" + inviteId, { method: "DELETE" });
    await loadAccess();
  };

  const removeAccess = async (userId: string) => {
    await fetchJson("/api/houses/" + house.id + "/access/" + userId, {
      method: "DELETE",
    });
    await loadAccess();
  };

  const disconnect = async (channelId: ChannelId) => {
    await fetchJson("/api/houses/" + house.id + "/credentials/" + channelId, {
      method: "DELETE",
    });
    await loadChannels();
  };

  const triggerImport = useCallback(
    async (channelId: ChannelId) => {
      await fetchJson("/api/houses/" + house.id + "/imports", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ channel: channelId }),
      });
    },
    [house.id],
  );

  const villavillaJobRunning = houseLogs.some(
    (log) => log.channel === "villavilla" && log.status === "running",
  );
  const latestVillavillaLog = houseLogs
    .filter((log) => log.channel === "villavilla")
    .reduce<WorkerJobLog | null>((current, log) => {
      if (!current) {
        return log;
      }
      return log.startedAt > current.startedAt ? log : current;
    }, null);
  const shouldPollVillavillaExternalId =
    connectedChannels.includes("villavilla") &&
    (externalHouseIds.villavilla ?? "").trim().length === 0 &&
    villavillaJobRunning;

  const wasVillavillaRunningRef = useRef(false);
  useEffect(() => {
    const wasRunning = wasVillavillaRunningRef.current;
    wasVillavillaRunningRef.current = villavillaJobRunning;
    if (wasRunning && !villavillaJobRunning) {
      void loadChannels();
    }
  }, [loadChannels, villavillaJobRunning]);

  useEffect(() => {
    if (!shouldPollVillavillaExternalId) {
      return;
    }
    void loadChannels();
    const intervalId = window.setInterval(() => {
      void loadChannels();
    }, 15_000);
    return () => window.clearInterval(intervalId);
  }, [loadChannels, shouldPollVillavillaExternalId]);

  const lastRefreshedVillavillaLogIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!latestVillavillaLog) {
      return;
    }
    if (lastRefreshedVillavillaLogIdRef.current === latestVillavillaLog.id) {
      return;
    }
    if (latestVillavillaLog.status === "running") {
      return;
    }
    if (!connectedChannels.includes("villavilla")) {
      return;
    }
    if ((externalHouseIds.villavilla ?? "").trim().length > 0) {
      return;
    }
    if (!latestVillavillaLog.finishedAt) {
      return;
    }
    const finishedAtMs = Date.parse(latestVillavillaLog.finishedAt);
    if (Number.isNaN(finishedAtMs)) {
      return;
    }
    const isRecent = Date.now() - finishedAtMs < 2 * 60_000;
    if (!isRecent) {
      return;
    }
    lastRefreshedVillavillaLogIdRef.current = latestVillavillaLog.id;
    void loadChannels();
  }, [connectedChannels, externalHouseIds.villavilla, latestVillavillaLog, loadChannels]);

  useEffect(() => {
    if (!pendingImportChannel || activeModalChannel) {
      return;
    }
    const channelId = pendingImportChannel;
    setPendingImportChannel(null);
    void (async () => {
      try {
        await triggerImport(channelId);
      } catch (importError) {
        setError(
          importError instanceof Error
            ? importError.message
            : t("import.failedStart", "Failed to start import"),
        );
      }
    })();
  }, [activeModalChannel, pendingImportChannel, triggerImport]);

  const updateWeekStartDay = async (nextWeekStartDay: WeekStartDay) => {
    setSettingsError(null);
    const previous = weekStartDay;
    setWeekStartDay(nextWeekStartDay);
    try {
      await fetchJson(`/api/houses/${house.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ weekStartDay: nextWeekStartDay }),
      });
      onRefresh();
    } catch (updateError) {
      setWeekStartDay(previous);
      setSettingsError(
        updateError instanceof Error
          ? updateError.message
          : t("house.weekUpdateFailed", "Failed to update booking week"),
      );
    }
  };

  const deleteHouse = async () => {
    if (isDeleting || deleteConfirmation.trim() !== house.name) {
      return;
    }
    setDeleteError(null);
    setIsDeleting(true);
    try {
      await fetchJson(`/api/houses/${house.id}`, { method: "DELETE" });
      setDeleteConfirmation("");
      onRefresh();
    } catch (deleteFailure) {
      setDeleteError(
        deleteFailure instanceof Error
          ? deleteFailure.message
          : t("house.deleteFailed", "Failed to delete house"),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const createCalendarFeed = async () => {
    setCalendarFeedError(null);
    setCalendarFeedNotice(null);
    setIsCalendarFeedSaving(true);
    try {
      const data = await fetchJson<CalendarFeedInfo>(`/api/houses/${house.id}/calendar-feed`, {
        method: "POST",
      });
      setCalendarFeedUrl(data.url);
      setCalendarFeedNotice(t("calendarFeed.created", "Calendar feed ready."));
    } catch (feedError) {
      setCalendarFeedError(
        feedError instanceof Error
          ? feedError.message
          : t("calendarFeed.createFailed", "Failed to create calendar feed"),
      );
    } finally {
      setIsCalendarFeedSaving(false);
    }
  };

  const regenerateCalendarFeed = async () => {
    setCalendarFeedError(null);
    setCalendarFeedNotice(null);
    setIsCalendarFeedSaving(true);
    try {
      const data = await fetchJson<CalendarFeedInfo>(
        `/api/houses/${house.id}/calendar-feed/regenerate`,
        {
          method: "POST",
        },
      );
      setCalendarFeedUrl(data.url);
      setCalendarFeedNotice(
        t(
          "calendarFeed.regenerated",
          "Calendar feed URL regenerated. Update existing subscriptions to use the new link.",
        ),
      );
    } catch (feedError) {
      setCalendarFeedError(
        feedError instanceof Error
          ? feedError.message
          : t("calendarFeed.regenerateFailed", "Failed to regenerate calendar feed"),
      );
    } finally {
      setIsCalendarFeedSaving(false);
    }
  };

  const copyCalendarFeed = async () => {
    if (!calendarFeedUrl) {
      return;
    }
    setCalendarFeedError(null);
    try {
      await navigator.clipboard.writeText(calendarFeedUrl);
      setCalendarFeedNotice(t("calendarFeed.copied", "Calendar feed URL copied to clipboard."));
    } catch {
      setCalendarFeedError(
        t("calendarFeed.copyFailed", "Failed to copy calendar feed URL to clipboard."),
      );
    }
  };

  const deleteMatches = deleteConfirmation.trim() === house.name;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{house.name}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {isReadonly ? (
          <p className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {t(
              "house.readonly",
              "This house is read-only. You can review import logs, but only full access users can update settings or manage access.",
            )}
          </p>
        ) : null}
        {!isReadonly ? (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              {channels.map((channel) => {
                const isConnected = connectedChannels.includes(channel.id);
                const channelLabel = t(
                  `channel.label.${channel.id}`,
                  channel.label,
                );
                const channelDescription = t(
                  `channel.description.${channel.id}`,
                  channel.description,
                );
                return (
                  <Card key={channel.id} className="border-dashed">
                    <CardHeader>
                      <CardTitle>{channelLabel}</CardTitle>
                      <p className="text-sm text-muted">{channelDescription}</p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {isConnected && channel.id === "villavilla" ? (
                        <p className="text-xs text-muted">
                          {t("channels.villavillaHouseId", "House no.")}{" "}
                          <span className="font-medium text-ink">
                            {externalHouseIds.villavilla ?? "—"}
                          </span>
                        </p>
                      ) : null}
                      {isConnected && channel.id === "admiralstrand" ? (
                        <p className="text-xs text-muted">
                          {t("channels.admiralstrandHouseId", "House ID")}{" "}
                          <span className="font-medium text-ink">
                            {externalHouseIds.admiralstrand ?? "—"}
                          </span>
                        </p>
                      ) : null}
                      {isConnected && channel.id === "eloverblik" ? (
                        <p className="text-xs text-muted">
                          {t("channels.eloverblikMeteringPoint", "Metering point")}{" "}
                          <span className="font-medium text-ink">
                            {externalHouseIds.eloverblik ?? "—"}
                          </span>
                        </p>
                      ) : null}
                      {!isConnected ? (
                        <Button onClick={() => setActiveModalChannel(channel.id)}>
                          {t("channels.connect", "Connect")}
                        </Button>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => triggerImport(channel.id)}
                          >
                            {t("channels.runImport", "Run import")}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => disconnect(channel.id)}
                          >
                            {t("channels.disconnect", "Disconnect")}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card className="border-dashed">
                <CardHeader>
                  <CardTitle>{t("house.bookingWeek", "Booking week")}</CardTitle>
                  <p className="text-sm text-muted">
                    {t(
                      "house.bookingWeekDescription",
                      "Choose week boundaries for coverage summaries.",
                    )}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <select
                    className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                    value={weekStartDay}
                    onChange={(event) =>
                      updateWeekStartDay(
                        event.target.value === "saturday" ? "saturday" : "friday",
                      )
                    }
                  >
                    <option value="friday">{t("house.weekFriday", "Friday -> Friday")}</option>
                    <option value="saturday">
                      {t("house.weekSaturday", "Saturday -> Saturday")}
                    </option>
                  </select>
                  {settingsError ? <p className="text-sm text-error">{settingsError}</p> : null}
                </CardContent>
              </Card>
              <Card className="border-dashed">
                <CardHeader>
                  <CardTitle>{t("calendarFeed.title", "Calendar feed")}</CardTitle>
                  <p className="text-sm text-muted">
                    {t(
                      "calendarFeed.description",
                      "Create a private subscription URL for Apple Calendar, Google Calendar, or Outlook.",
                    )}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {calendarFeedUrl ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Input
                          value={calendarFeedUrl}
                          readOnly
                          aria-label={t("calendarFeed.url", "Calendar feed URL")}
                          className="min-w-0 flex-1"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 w-9 shrink-0 px-0"
                          onClick={() => void copyCalendarFeed()}
                          aria-label={t("calendarFeed.copy", "Copy URL")}
                          title={t("calendarFeed.copy", "Copy URL")}
                        >
                          <svg
                            aria-hidden="true"
                            focusable="false"
                            viewBox="0 0 24 24"
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="9" y="9" width="13" height="13" rx="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={calendarFeedUrl}
                          target="_blank"
                          rel="noopener"
                          className="btn-outline inline-flex h-8 items-center justify-center rounded-md px-3 text-xs font-medium brand-focus"
                        >
                          {t("calendarFeed.open", "Open feed")}
                        </a>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void regenerateCalendarFeed()}
                          disabled={isCalendarFeedSaving}
                        >
                          {isCalendarFeedSaving
                            ? t("calendarFeed.regenerating", "Regenerating...")
                            : t("calendarFeed.regenerate", "Regenerate URL")}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button onClick={() => void createCalendarFeed()} disabled={isCalendarFeedSaving}>
                      {isCalendarFeedSaving
                        ? t("calendarFeed.creating", "Creating...")
                        : t("calendarFeed.create", "Create calendar feed")}
                    </Button>
                  )}
                  {calendarFeedNotice ? (
                    <p className="text-sm text-muted">{calendarFeedNotice}</p>
                  ) : null}
                  {calendarFeedError ? (
                    <p className="text-sm text-error">{calendarFeedError}</p>
                  ) : null}
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card className="border-dashed">
                <CardHeader>
                  <CardTitle>{t("invite.title", "Invite user")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form className="space-y-3" onSubmit={sendInvite}>
                    <Input
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      placeholder={t("invite.emailPlaceholder", "user@example.com")}
                      type="email"
                      required
                    />
                    <select
                      className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                      value={inviteRole}
                      onChange={(event) =>
                        setInviteRole(event.target.value === "full" ? "full" : "readonly")
                      }
                    >
                      <option value="readonly">{t("invite.readonly", "Read-only")}</option>
                      <option value="full">{t("invite.full", "Full access")}</option>
                    </select>
                    {error ? <p className="text-sm text-error">{error}</p> : null}
                    <Button size="sm" type="submit">
                      {t("invite.send", "Send invite")}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card className="border-dashed">
                <CardHeader>
                  <CardTitle>{t("access.current", "Current access")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("access.user", "User")}</TableHead>
                        <TableHead>{t("access.role", "Role")}</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accessList.map((access) => (
                        <TableRow key={access.id}>
                          <TableCell>{access.email ?? access.userId}</TableCell>
                          <TableCell>
                            {access.role === "full"
                              ? t("invite.full", "Full access")
                              : t("invite.readonly", "Read-only")}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeAccess(access.userId)}
                            >
                              {t("access.remove", "Remove")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            <Card className="border-dashed">
              <CardHeader>
                <CardTitle>{t("invite.pending", "Pending invites")}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("invite.email", "Email")}</TableHead>
                      <TableHead>{t("access.role", "Role")}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invites.map((invite) => (
                      <TableRow key={invite.id}>
                        <TableCell>{invite.email}</TableCell>
                        <TableCell>
                          {invite.role === "full"
                            ? t("invite.full", "Full access")
                            : t("invite.readonly", "Read-only")}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => revoke(invite.id)}>
                            {t("invite.revoke", "Revoke")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card className="border-dashed">
              <CardHeader>
                <CardTitle>{t("house.dangerZone", "Danger zone")}</CardTitle>
                <p className="text-sm text-muted">
                  {t(
                    "house.deleteWarning",
                    "Deleting a house removes all linked data. This cannot be undone.",
                  )}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted">
                  {t("house.deleteConfirmPrefix", "Type")}{" "}
                  <span className="font-semibold">{house.name}</span>{" "}
                  {t("house.deleteConfirmSuffix", "to confirm.")}
                </p>
                <Input
                  value={deleteConfirmation}
                  onChange={(event) => setDeleteConfirmation(event.target.value)}
                  placeholder={t("house.deletePlaceholder", 'Type "{houseName}" to confirm', {
                    houseName: house.name,
                  })}
                />
                {deleteError ? <p className="text-sm text-error">{deleteError}</p> : null}
                <Button
                  variant="destructive"
                  onClick={deleteHouse}
                  disabled={!deleteMatches || isDeleting}
                >
                  {isDeleting
                    ? t("common.deleting", "Deleting...")
                    : t("house.delete", "Delete house")}
                </Button>
              </CardContent>
            </Card>
          </>
        ) : null}
        <ImportLogs logs={houseLogs} onRefresh={onRefreshLogs} />
        {activeModalChannel && !isReadonly ? (
          <ChannelConnectModal
            channel={channels.find((item) => item.id === activeModalChannel) ?? channels[0]}
            houseId={house.id}
            onClose={() => setActiveModalChannel(null)}
            onConnected={(channelId) => {
              void loadChannels();
              onRefresh();
              setActiveModalChannel(null);
              setPendingImportChannel(channelId);
            }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
};
